import React from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Dispatch } from 'hellospa/redux/types';
import HelloModal from 'common/components/hello-modal';
import HfReactHelper from 'common/utils/hf-react-helper';
import { unreachable } from 'hellospa/ts-utils';
import {
  UploadIntegrationType, ExternalServiceAuthStatusResponse,
} from 'hellospa/page/prep-and-send/data/types';
import {
  externalFolderContents, externalServiceAuthStatus, setIntegrationStatus,
} from 'hellospa/page/prep-and-send/data/actions/integration';
import { createBannerMessage } from 'hellospa/components/notification-banner/data/actions';
import { responseMessages } from './messages';
import { NotificationBannerType } from 'hellospa/components/notification-banner/data/types';

type Props = {
  integration: UploadIntegrationType,
  isOpen: boolean,
  integrationIcon: string,
  onClose: () => void,
}

type State = {
  authUrl: string,
  boxReAuth: boolean,
  googleReAuth: boolean,
  processing: boolean,
  timeoutCount: number,
}

const MAX_SET_TIMEOUT_COUNT = 20;
export default function ReAuthModal({
  integration,
  isOpen,
  integrationIcon,
  onClose,
}: Props) {
  const intl = useIntl();
  const dispatch = useDispatch<Dispatch>();
  const [state, setState] = React.useState<State>({
    authUrl: '',
    boxReAuth: false,
    googleReAuth: false,
    processing: false,
    timeoutCount: 0,
  });

  // clicking on the modal button triggers status check along with the
  // user being redirected to authenticate & enable the integration
  const onContinue = React.useCallback(() => {
    setState((state) => ({
      ...state,
      processing: true,
    }));
  }, []);

  const onReauth = React.useCallback(async () => {
    // retrieve the integration contents here
    const data = await dispatch(
      externalFolderContents(integration, 0, '0'),
    );

    // this is to handle cases where the prep & send page is open, but
    // the integration is activated from the integration page
    if (!('authUrl' in data)) {
      onContinue();
      return;
    }

    setState((state) => ({
      ...state,
      authUrl: data.authUrl,
      boxReAuth: data.boxReAuth,
      googleReAuth: data.googleReAuth,
    }));
  }, [dispatch, integration, onContinue]);

  const mounted = React.useRef(false);
  React.useEffect(() => {
    if (!mounted.current) {
      onReauth();
    }

    return () => {
      mounted.current = true;
    };
  }, [onReauth]);

  const doPoll = React.useCallback(async () => {
    // asserting type here because the response should be as expected if it's here
    const response = await dispatch(
      externalServiceAuthStatus(integration),
    ) as ExternalServiceAuthStatusResponse;
    if (response.authorized) {
      setState((state) => ({
        ...state,
        processing: false,
      }));
      // dispatch action to redux here
      await dispatch(setIntegrationStatus(integration, true));

      return;
    } else if (response.failed) {
      setState((state) => ({
        ...state,
        processing: false,
      }));

      dispatch(createBannerMessage(
        responseMessages.defaultError,
        NotificationBannerType.Err,
        { authResultError: response.errorMessage || '' },
      ));
      return;
    }

    // increase count to update state so useEffect gets triggered again
    setState((state) => ({
      ...state,
      timeoutCount: state.timeoutCount + 1,
    }));
  }, [dispatch, integration]);

  React.useEffect(() => {
    let timeout: NodeJS.Timeout;
    // if it's processing & not reached the max timeout count, keep polling
    // to see if the integration is authorized for use
    if (state.processing && state.timeoutCount <= MAX_SET_TIMEOUT_COUNT) {
      timeout = setTimeout(async () => {
        doPoll();
      }, 2500);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [state.processing, state.timeoutCount, doPoll]);

  const brandName = HfReactHelper.isHelloSign() ? 'HelloSign' : 'HelloFax';
  let message;
  switch (integration) {
    case UploadIntegrationType.Box: {
      // switch message between an old integration and the newer one
      const text = state.boxReAuth ? responseMessages.boxReAuth : responseMessages.box;
      message = intl.formatMessage(text, {
        brandName,
      });
      break;
    }
    case UploadIntegrationType.Evernote: {
      message = intl.formatMessage(responseMessages.evernote, {
        brandName,
      });
      break;
    }
    case UploadIntegrationType.GoogleDrive: {
      // switch message between an old integration and the newer one
      const text = state.googleReAuth
        ? responseMessages.googleDriveReAuth
        : responseMessages.googleDrive;
      message = intl.formatMessage(text, {
        brandName,
      });
      break;
    }
    case UploadIntegrationType.OneDrive: {
      message = intl.formatMessage(responseMessages.oneDrive, {
        brandName,
      });
      break;
    }
    case UploadIntegrationType.Dropbox: {
      message = intl.formatMessage(responseMessages.dropbox, {
        brandName,
      });
      break;
    }
    default:
      unreachable(integration);
  }

  return (
    <HelloModal
      isOpen={isOpen}
      contentLabel={
        <img src={integrationIcon} />
      }
      onRequestClose={onClose}
      buttons={[
        {
          type: 'primary',
          text: 'Continue',
          // disabling the button until a URL is returned, otherwise clicking on it
          // just opens the prep & send on a different window
          disabled: state.authUrl === '',
          'data-qa-ref': 'reauth-modal-continue',
          onClick: () => onContinue(),
          buttonLink: state.authUrl,
          openInNewWindow: true,
        },
      ]}
    >
      {message}
    </HelloModal>
  );
}
